// Package Imports
import { useTable } from '@refinedev/antd';
import { BaseRecord, useOne } from '@refinedev/core';
import { Card, Col, Row, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

// Project Imports
import apiEndpoint from 'core/common/apiEndPoints';

import ContestHistoryOverview from 'pages/contest/contestHistory/contestHistoryOverview';
import getUnifiedList from 'pages/myTask/customDetails/contest/utils/fetchUnified';
import getUnifiedColumnList from 'pages/myTask/customDetails/contest/utils/getUnifiedColumnList';
import useFormatCurrency from 'core/utils/formatCurrency';
import useFormatNumber from 'core/utils/formatNumber';
import useFormatPoints from 'core/utils/formatPoints';

const ContestHistoryDetails = () => {
  const { Title } = Typography;
  const { id, calId } = useParams();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatPoints = useFormatPoints();

  const { tableProps } = useTable({
    resource: `${apiEndpoint.fetchContestRuleData}/?id=${calId}`,
    dataProviderName: 'contestProvider',
    queryOptions: {
      enabled: !!calId,
    },
    pagination: {
      mode: 'server',
    },
    syncWithLocation: false,
  });
  const contestList =
    (tableProps?.dataSource as BaseRecord)?.results?.results || {};
  const contestUserDetails =
    (tableProps?.dataSource as BaseRecord)?.results?.contest_user_details || [];
  const totalRecords = (tableProps?.dataSource as any)?.results?.count || 0;

  const { data: contestData, isFetching: contestLoading } = useOne({
    dataProviderName: 'contestProvider',
    resource: `${apiEndpoint.contest}`,
    id,
    queryOptions: {
      enabled: !!id,
    },
  });
  const contestDetail = contestData?.data?.results || {};
  const contestType = contestDetail?.contest_type
    ? contestDetail.contest_type[0].toUpperCase() +
      contestDetail.contest_type.slice(1)
    : 'Leaderboard';

  const unifiedList = getUnifiedList();
  const unifiedColumnList = getUnifiedColumnList(contestDetail?.unified_id);

  const payoutColumns =
    contestList?.headers?.map((item) => ({
      title: item.label,
      dataIndex: item.key,
      key: item.key,
      render: (text) => {
        if (item.data_type === 'date') {
          const formattedDateRange = dayjs(text).format('MMM DD, YYYY');
          if (text) {
            return formattedDateRange;
          }
        }
        if (item.key === 'prize' || item.key === 'prize_type') {
          if (text === 0 || text === 'default') {
            return '-';
          }
        }
        if (item.data_type === 'currency') {
          return formatCurrency(text);
        }
        if (item.data_type === 'number') {
          return formatNumber(text);
        }
        if (item.data_type === 'point') {
          return formatPoints(text);
        }
        return text;
      },
    })) || [];

  return (
    <Card>
      <Title level={4}>Contest History Details</Title>

      <Row
        gutter={[24, 0]}
        className="mt-8 mb-4"
        style={{ alignItems: 'stretch' }}
      >
        <Col xs={24} md={12} lg={12}>
          <Card
            className="border-0 overflow-y-auto"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              height: '100%',
            }}
          >
            {unifiedList && unifiedColumnList ? (
              <ContestHistoryOverview
                contestLoading={contestLoading}
                contestDetails={contestDetail}
                unifiedList={unifiedList}
                unifiedColumnList={unifiedColumnList}
                contestUserDetails={contestUserDetails}
                contestList={contestList}
              />
            ) : null}
          </Card>
        </Col>
        <Col xs={24} md={12} lg={12}>
          <Card
            className="border-0"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <h6>
              {contestType.charAt(0).toUpperCase() + contestType.slice(1)}
            </h6>
            <Table
              {...tableProps}
              columns={payoutColumns}
              dataSource={contestList?.dataSource}
              bordered
              rowKey={(record) => record?.taskId}
              pagination={{
                ...tableProps.pagination,
                total: totalRecords,
                showSizeChanger: true,
              }}
              className="mt-3"
            />
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default ContestHistoryDetails;
